import { Component, Input, ViewChild, OnInit, AfterViewInit, TemplateRef } from "@angular/core";
import { trigger, transition, style, animate } from "@angular/animations";
import { ApiService } from "@app/Services/APIService";
import { GlobalFunctions } from "@app/Global/GlobalFunctions";
import { NotifyService } from "@app/Services/NotifyService";
import { OverlayPanel } from "primeng/overlaypanel";
import { MatDialog } from "@angular/material/dialog";
import { PaymentManagementModify } from "@app/Components/Loan/PaymentManagementModify/PaymentManagementModify";
import { ConfirmModal } from "@app/Components/Loan/ConfirmModal/ConfirmModal";
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { AccountsControllerMethods } from "@app/Global/EnumManager";
import { LoanIndex } from "@app/Components/Loan/LoanIndex/LoanIndex";
import { ClientSelection } from "@app/Components/Loan/ClientSelection/ClientSelection";

@Component({
    selector: "PaymentManagement",
    templateUrl: "./PaymentManagement.html",
    styleUrls: ["./PaymentManagement.scss"],
    animations: [
        trigger("fadeIn", [
            transition(":enter", [
                style({ opacity: "0" }),
                animate("0.1s ease-out", style({ opacity: "1" })),
            ]),
        ]),
        trigger("fadeOut", [
            transition(":leave", [
                style({ opacity: "1" }),
                animate("0.1s ease-out", style({ opacity: "0" })),
            ]),
        ]),
    ]
})

export class PaymentManagement implements OnInit, AfterViewInit {

    @Input() LoanIndex: LoanIndex;

    //Overlay panel reference to handle new payment setup
    @ViewChild("overlayPanel_NewPayment") OverlayPanelNewPayment: OverlayPanel;

    public InitialPageLoad = true;

    //For tracking if a server update request is in progress
    public IsGenerateInProgress = false;

    // Modal Identifier
    public ModalIdentifier: string;

    //Disable the Generate Letter button
    public EnableGenerateLetterButton = false;

    // Modal Identifier for Fee
    public FeeModalIdentifier: string;

    // Target Account ID
    public TargetAccountID = 548754;

    // Current active tab - Set to "manageDirectDebit" by default
    public CurrentTab: string = "manageDirectDebit";

    //Account Bank Details
    public BankAccountDetails: any;

    //Salary Credit Details
    public SalaryCreditDetails: any;

    //Flag to enable Direct Credit / Salary Credit section
    public ShowDirectCreditSalaryCredit = false;

    //BPAY Details
    public BPAYDetails: any;

    //Linked Account Details
    public LinkedAccountDetails = [];

    //Scheduled Payment Details
    public ScheduledPaymentDetails = [];

    //Payment Type Options
    public PaymentMethodOptions = [];

    //Schedule Frequency Options
    public ScheduleFrequencyOptions = [];

    //Currently selected payment GUID
    public SelectedPaymentGUID: string = null;

    // Flag to indicate if a payment is being edited
    public IsPaymentBeingEdited: boolean = false;

    //Direct Debit Enabled/Disabled flag
    public IsDirectDebitEnabled: boolean = true;

    //Monthly Instalment Amount
    public MonthlyInstalmentAmount: number;

    //Repayment Amounts
    public MonthlyRepaymentAmount: number;
    public FortnightlyRepaymentAmount: number;
    public WeeklyRepaymentAmount: number;

    //Direct Debit Tasks for target account
    public DirectDebitEnabled: any;

    //This is a placeholder, should be set based on actual data
    public InstalmentDueDate = "2025-07-28";

    //Schedule Payment Frequency
    public NewSchedulePaymentFrequency: string = "";

    //Schedule Payment Frequency GUID
    public MonthlyPaymentFrequencyGUID: string = "";

    //Monthly Instalment Due Date
    public NewInstalmentDueDate: string = "";

    //Payment Transaction Options
    public PaymentTransactionOptions = []

    //Charge Transaction Options
    public ChargeTransactionOptions = [];

    //Customer Nominated Payment Frequency Options
    public CustomerNominatedFrequencyOptions = [];

    //Allow deleting the Scheduled Payment
    public AllowScheduledPaymentDelete = false;

    //Allow Viewing Inter Account Transfer Button
    public ViewProcessInterAccountTransfer: boolean = false;

    //Allow Processing Inter Account Transfer
    public AllowInterAccountTransfer: boolean = false;

    //Text display limit
    public TextDisplayLimit = 24;

    //Dialog reference
    public DialogRef;

    //Selected Payment
    public SelectedPayment: any;

    //For displaying total fee in the dialog
    public TotalConsolidatedFeeAmount_lbl: string = "";

    //Total Fee amount
    public TotalFeeAmount: number = 0;

    //Constructor
    constructor(
        private apiService: ApiService,
        public globalFunctions: GlobalFunctions,
        private notifyService: NotifyService,
        private clientDataStore: ClientDataStore,
        private dialog: MatDialog) { }

    ngOnInit(): void {

        //TODO: This needs to be set from API response
        this.IsDirectDebitEnabled = true;
    }

    //This will be called after the view has been loaded
    ngAfterViewInit() {

        //Run it on initial load only
        if (this.InitialPageLoad) {

            //We still need a short delay even this method is added on ngAfterViewInit
            this.globalFunctions.delay(50).then(() => {

                // Initialize component
                this.AccountPaymentDetails_Get();

                //Set class variable initialPageLoad to false
                this.InitialPageLoad = false;

                //Check permissions for deleting scheduled payments
                if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "PaymentManagement", "Delete") === true) {
                    this.AllowScheduledPaymentDelete = true;
                }

                // Check read claim for inter account transfer processing button
                if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "PaymentInterAccountTransfer", "Read") === true) {
                    this.ViewProcessInterAccountTransfer = true;
                }

                // Check edit claim for inter account transfer processing button
                if (this.globalFunctions.Claim_VerifyPermission(this.clientDataStore, "PaymentInterAccountTransfer", "Edit") === true) {
                    this.AllowInterAccountTransfer = true;
                }
            });
        }
    }

    //Disable Generate Letter button
    public CSS_Disable(): string {

        if (!this.EnableGenerateLetterButton) {
            return "glb_clearButton";
        }
        return "";
    }

    //API call to enable and disable scheduled payment
    public DirectDebitFlag_Update(enableDirectDebit: any, paymentIndex: any): void {

        const apiRequest = {
            AccountID: this.TargetAccountID,
            EnabledDirectDebit: enableDirectDebit
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.SetScheduledDirectDebitStatus], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {
                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {

                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    if (response != null) {
                        //Show success notification
                        if (response.EnabledDirectDebit == false) {

                            //Set IsEnabled to false
                            this.ScheduledPaymentDetails[paymentIndex].IsEnabled = response.EnabledDirectDebit;

                            //Show success notification
                            this.notifyService.Success_Show("Scheduled payment has been disabled", "Scheduled Payment");

                            // Prompt user if they want to set up a Customer Nominated Payment
                            const confirmModalDialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Set Up Payment", 0, true, false);

                            // Set prompt message
                            confirmModalDialogRef.DialogRef.componentInstance.htmlContent = "Scheduled payment has been disabled. Would you like to set up a One Off or Customer Nominated Payment?";

                            // Set button text to "Yes"
                            confirmModalDialogRef.DialogRef.componentInstance.YesButtonText = "Yes";

                            // Handle the user's response
                            confirmModalDialogRef.DialogRef.afterClosed().subscribe((result: boolean) => {
                                if (result === true) {

                                    // If user clicks "Yes", launch the Customer Nominated Payment setup
                                    this.CustomerNominatedPaymentSetup_Click();
                                }
                                else {
                                    //Refresh system tasks once direct debit is disabled
                                    this.LoanIndex.Entity_FullRefresh("PendingTasks");

                                    //Refresh notes once direct debit is disabled
                                    this.LoanIndex.Entity_FullRefresh("LoanNotes");
                                }
                            });
                        }
                        else {
                            // Set IsEnabled to true
                            this.ScheduledPaymentDetails[paymentIndex].IsEnabled = response.EnabledDirectDebit;

                            //Refresh system tasks once direct debit is disabled
                            this.LoanIndex.Entity_FullRefresh("PendingTasks");

                            //Refresh notes once direct debit is disabled
                            this.LoanIndex.Entity_FullRefresh("LoanNotes");

                            // Show success notification
                            this.notifyService.Success_Show("Scheduled payment has been enabled", "Scheduled Payment");
                        }
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;

                    return;
                }
            });
    }
    //Get Account details from the server
    public AccountPaymentDetails_Get(): void {

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct the request and send it to the server
        const apiRequest = { AccountID: this.TargetAccountID };

        //API to get account payment details
        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.GetAccountPaymentDetails], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {
                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    if (response != null) {

                        //Direct Debit Task for the target product
                        this.DirectDebitEnabled = response.DirectDebitTaskEnabled;

                        //Set the Salary Credit payment details with the server response
                        if (response.DirectSalaryCreditAccountDetails != null) {
                            this.SalaryCreditDetails = {
                                GUID: response.DirectSalaryCreditAccountDetails.BankTransferGUID,
                                BSBNumber: response.DirectSalaryCreditAccountDetails.CollectionAccountBSBNo,
                                AccountNumber: response.DirectSalaryCreditAccountDetails.CollectionAccountNumber,
                                AccountName: this.globalFunctions.HTMLUnescape(response.DirectSalaryCreditAccountDetails.CollectionAccountName)
                            };
                        }

                        //Set Direct Credit / Salary Credit flag to false if bank details are not provided
                        if (this.SalaryCreditDetails != null) {
                            if (!this.globalFunctions.isEmpty(this.SalaryCreditDetails.BSBNumber) || !this.globalFunctions.isEmpty(this.SalaryCreditDetails.AccountNumber) || !this.globalFunctions.isEmpty(this.SalaryCreditDetails.AccountName)) {
                                this.ShowDirectCreditSalaryCredit = true;
                            }
                        }

                        //Set the BPAY payment details with the server response
                        if (response.BPAYAccountDetails != null) {
                            this.BPAYDetails = {
                                GUID: response.BPAYAccountDetails.FieldTypeGUID,
                                BillerCode: response.BPAYAccountDetails.BpayBillerCode,
                                CRN: response.BPAYAccountDetails.BpayAccountNumber,
                                AccountName: this.globalFunctions.HTMLUnescape(response.BPAYAccountDetails.BpayAccountName)
                            };
                        }

                        //Set the scheduled payment details with the server response
                        if (response.LinkedAccountforPaymentManagementResult != null) {
                            response.LinkedAccountforPaymentManagementResult.forEach((linkedAccount: { AccountID: any; ProductType: any; AccountName: any; AvailableBalance: any; }) => {
                                this.LinkedAccountDetails.push({
                                    AccountID: linkedAccount.AccountID,
                                    ProductType: linkedAccount.ProductType,
                                    AccountID_lbl: this.globalFunctions.HTMLUnescape(linkedAccount.AccountName),
                                    AvailableBalance_lbl: linkedAccount.AvailableBalance != null ? this.globalFunctions.customDataTypeParser(linkedAccount.AvailableBalance, "currency.2", "aus") : ""
                                });
                            });
                        }

                        //Set the target bank details with the server response
                        if (response.DirectDebitBankAccount.AccountBSBNo != null) {
                            this.BankAccountDetails = response.DirectDebitBankAccount;

                            //Removes ARMNet type of encoding from a string
                            this.BankAccountDetails.AccountName = this.globalFunctions.HTMLUnescape(this.BankAccountDetails.AccountName);
                            this.BankAccountDetails.BankName = this.globalFunctions.HTMLUnescape(this.BankAccountDetails.BankName);

                            //Enable Generate Letter button
                            this.EnableGenerateLetterButton = true;
                        }

                        //Set the scheduled payment details with the server response
                        if (response.ScheduledPaymentDetails != null) {
                            response.ScheduledPaymentDetails.forEach((scheduledPayment: { ProductLoanTransferGUID: any; Frequency: any; FrequencyTypeClassification: any, DirectDebitAmount: any; InstalmentAmount: any; NextPaymentDueDate: any; InstalmentDueDate: any; ExpiryDate: any; PaymentType: any; Notes: any; AllowEditing: any; PendingTaskGUID: any; IsDisabled: any; FrequencyTaskGUID: any, ConsolidatedFeeAmount: any, ScheduledPaymentFeeList: any }) => {

                                //Push the scheduled payment details into an array
                                this.ScheduledPaymentDetails.push(this.SchedulePaymentDetail_Construct(scheduledPayment));
                            });
                        }

                        //Set the frequency options for customer nominated payment
                        if (response.CustomerNominatedFrequencyOptions != null) {
                            this.CustomerNominatedFrequencyOptions = response.CustomerNominatedFrequencyOptions;
                        }
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;

                    // Show success notification
                    this.notifyService.Success_Show("Account payment details retrieved successfully", "Account Payment Details");

                    return;
                }
            });
    }

    //Method to handle active tab CSS 
    public IsActiveTab_Check(tabName: string): boolean {
        return this.CurrentTab === tabName;
    }

    //Method to generate letter for payment details
    public BankDetailsLetter_Generate(): void {

        //Display Client Selection modal first
        //Get the component of this modal, and set a property on it
        const clientSelectionRef = this.globalFunctions.FeatureModal_Launch(ClientSelection, this.globalFunctions.GetFeatureModalConfig('40%'), this.dialog, "Client", this.TargetAccountID, true, false);

        clientSelectionRef.DialogRef.componentInstance.LoanIndex = this.LoanIndex;
        clientSelectionRef.DialogRef.componentInstance.GenerateLetterButtonID = "GenerateDDConfirmationLetter";
        clientSelectionRef.DialogRef.componentInstance.ToastTitle = "Direct Debit Confirmation Letter";
        clientSelectionRef.DialogRef.componentInstance.GenerateLetterMethodName = "DDConfirmationLetter_Generate()";
    }

    //Method to generate letter for payment details
    public BPAYLetter_Generate(): void {

        //Display Client Selection modal first
        //Get the component of this modal, and set a property on it
        const clientSelectionRef = this.globalFunctions.FeatureModal_Launch(ClientSelection, this.globalFunctions.GetFeatureModalConfig('40%'), this.dialog, "Client", this.TargetAccountID, true, false);

        clientSelectionRef.DialogRef.componentInstance.LoanIndex = this.LoanIndex;
        clientSelectionRef.DialogRef.componentInstance.GenerateLetterButtonID = "GenerateBPAYLetter";
        clientSelectionRef.DialogRef.componentInstance.ToastTitle = "BPAY Letter";
        clientSelectionRef.DialogRef.componentInstance.GenerateLetterMethodName = "BPAYLetter_Generate()";
    }

    //Method to handle row selection
    public SelectPayment_Row(guid: string): void {

        if (this.SelectedPaymentGUID === guid) {
            // If clicking the same row, deselect it
            this.SelectedPaymentGUID = null;
        } else {
            // Otherwise, select the clicked row
            this.SelectedPaymentGUID = guid;
        }
    }

    //Launch the Payment Management component for different modify types
    public PaymentManagementModify_Launch(modifyType: string = "", selectedPayment?: any): void {

        //Check if modifyType is provided and not empty
        if (this.globalFunctions.isEmpty(modifyType)) {
            return;
        }

        //Launch the Payment Management Modify modal
        const paymentManagementModifyModalRef = this.globalFunctions.FeatureModal_Launch(PaymentManagementModify, this.globalFunctions.GetFeatureModalConfig("40%", false, false, "glb_fixedHeightDialog"), this.dialog, "Payment Management", this.TargetAccountID, true, false);

        //Set the TargetAccountID for the modal
        paymentManagementModifyModalRef.DialogRef.componentInstance.TargetAccountID = this.TargetAccountID;

        //Check the modifyType & pass the necessary data to the modal as per modify type
        if (modifyType === "EditBankAccount") {

            //This is for adding or editing a bank account
            const isEditing = !this.globalFunctions.isEmpty(this.BankAccountDetails);

            //Set page title based on whether we"re adding or editing
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = isEditing ? "Edit Bank Account" : "Add Bank Account";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "EditBankAccount";

            //If we are editing, we can pass the existing bank account details
            if (isEditing) {
                paymentManagementModifyModalRef.DialogRef.componentInstance.BankAccountDetails = this.BankAccountDetails;
            }

            //Subscribe to the dialog close event to get the updated data
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { updatedBankAccountDetails: any; }) => {
                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.updatedBankAccountDetails)) {
                    // The API call has been moved to the child component
                    // Now we just need to update our local BankAccountDetails variable with the result

                    //Update the bank account details variable
                    this.BankAccountDetails = result.updatedBankAccountDetails;

                    //Refresh bank details once bank account is edited
                    this.LoanIndex.Entity_FullRefresh("BankDetails");

                    //Make sure to enable Generate Letter button
                    this.EnableGenerateLetterButton = true;

                    //Refresh notes once once bank account is edited
                    this.LoanIndex.Entity_FullRefresh("LoanNotes");
                }
            });
        }
        else if (modifyType === "ProcessInterAccountTransfer") {

            //Set the page title and modify type
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = "Process Inter Account Transfer";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "ProcessInterAccountTransfer";

            //Pass the LinkedAccountDetails to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.LinkedAccountDetails = this.LinkedAccountDetails;

            //Subscribe to the dialog close event to get the updated payment details
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { updatedLinkedAccounts: any }) => {

                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.updatedLinkedAccounts)) {

                    //Reset the LinkedAccountDetails array
                    this.LinkedAccountDetails = [];

                    //Push the updated linked accounts to the LinkedAccountDetails array
                    result.updatedLinkedAccounts.forEach((linkedAccount: { AccountID: any; ProductType: any; AccountName: any; AvailableBalance: any; }) => {
                        this.LinkedAccountDetails.push({
                            AccountID: linkedAccount.AccountID,
                            ProductType: linkedAccount.ProductType,
                            AccountID_lbl: this.globalFunctions.HTMLUnescape(linkedAccount.AccountName),
                            AvailableBalance_lbl: linkedAccount.AvailableBalance != null ? this.globalFunctions.customDataTypeParser(linkedAccount.AvailableBalance, "currency.2", "aus") : ""
                        });
                    });
                }
            });
        }
        else if (modifyType === "CustomerNominatedPayment") {

            //Set page title
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = "Set Up Customer Nominated Payment";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "CustomerNominatedPayment";

            //Set the applicable Charge and Payment Transactions
            paymentManagementModifyModalRef.DialogRef.componentInstance.ChargeTransactionOptions = this.ChargeTransactionOptions;
            paymentManagementModifyModalRef.DialogRef.componentInstance.PaymentTransactionOptions = this.PaymentTransactionOptions;
            paymentManagementModifyModalRef.DialogRef.componentInstance.CustomerNominatedFrequencyOptions = this.CustomerNominatedFrequencyOptions;

            //Subscribe to the dialog close event to get the updated payment details
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { updatedPaymentDetails: any }) => {
                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.updatedPaymentDetails)) {

                    //Construct a new payment object with the updated details
                    const updatedScheduledPaymentDetails = this.SchedulePaymentDetail_Construct(result.updatedPaymentDetails);

                    //Update the payment in the array
                    this.ScheduledPaymentDetails.push(updatedScheduledPaymentDetails);

                    //Refresh system tasks once customer nominated payment is changed
                    this.LoanIndex.Entity_FullRefresh("PendingTasks");

                    //Refresh notes once customer nominated payment is changed
                    this.LoanIndex.Entity_FullRefresh("LoanNotes");
                }
            });
        } else if (modifyType === "ScheduledPayment") {

            //Set page title
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = "Set Up Scheduled Payment";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "ScheduledPayment";

            //Pass new scheduled payment default values to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.InstalmentDueDate = this.InstalmentDueDate;
            paymentManagementModifyModalRef.DialogRef.componentInstance.MonthlyInstalmentAmount = this.MonthlyInstalmentAmount;
            paymentManagementModifyModalRef.DialogRef.componentInstance.MonthlyRepaymentAmount = this.MonthlyRepaymentAmount;
            paymentManagementModifyModalRef.DialogRef.componentInstance.FortnightlyRepaymentAmount = this.FortnightlyRepaymentAmount;
            paymentManagementModifyModalRef.DialogRef.componentInstance.WeeklyRepaymentAmount = this.WeeklyRepaymentAmount;
            paymentManagementModifyModalRef.DialogRef.componentInstance.NewSchedulePaymentFrequency = this.NewSchedulePaymentFrequency;
            paymentManagementModifyModalRef.DialogRef.componentInstance.MonthlyPaymentFrequencyGUID = this.MonthlyPaymentFrequencyGUID;
            paymentManagementModifyModalRef.DialogRef.componentInstance.NewInstalmentDueDate = this.NewInstalmentDueDate;

            //Pass payment type options to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.PaymentMethodOptions = this.PaymentMethodOptions;

            //Pass the schedule frequency options to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.ScheduleFrequencyOptions = this.ScheduleFrequencyOptions;

            //Pass the existing scheduled payments to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.ScheduledPaymentDetails = this.ScheduledPaymentDetails;

            //Subscribe to the dialog close event to get the updated payment details
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { updatedPaymentDetails: any }) => {
                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.updatedPaymentDetails)) {

                    //Remove any existing scheduled payment
                    const existingIndex = this.ScheduledPaymentDetails.findIndex(payment => payment.PaymentType === "Scheduled");
                    if (existingIndex !== -1) {
                        this.ScheduledPaymentDetails.splice(existingIndex, 1);
                    }

                    //Construct a new payment object with the updated details
                    const updatedScheduledPaymentDetails = this.SchedulePaymentDetail_Construct(result.updatedPaymentDetails);

                    //Push this newly created scheduled payment
                    this.ScheduledPaymentDetails.push(updatedScheduledPaymentDetails);

                    //Reset the selected payment GUID
                    this.SelectedPaymentGUID = null;

                    //Reset editing state
                    this.IsPaymentBeingEdited = false;

                    //Show success notification
                    this.notifyService.Success_Show("Scheduled payment details updated successfully", "Scheduled Payment");

                    //Refresh system tasks once scheduled payment is changed
                    this.LoanIndex.Entity_FullRefresh("PendingTasks");

                    //Refresh notes once scheduled payment is changed
                    this.LoanIndex.Entity_FullRefresh("LoanNotes");
                }
            });
        } else if (modifyType === "DeferPayment") {

            //Set page title
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = "Defer Scheduled Payment";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "DeferPayment";

            //If a payment was passed, set it as the selected payment
            if (!this.globalFunctions.isEmpty(selectedPayment)) {

                //Pass the selected payment details to the modal
                paymentManagementModifyModalRef.DialogRef.componentInstance.SelectedPaymentDetails = selectedPayment;
            }

            //Subscribe to the dialog close event
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { deferredPaymentDetails: { UpdatedPaymentDetails: any; DeferMonths: any; NextPaymentDate: any }; }) => {

                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.deferredPaymentDetails)) {

                    //Get the updated payment details
                    const updatedPaymentDetails = result.deferredPaymentDetails.UpdatedPaymentDetails;
                    const nextPaymentDate = result.deferredPaymentDetails.NextPaymentDate;

                    //Find and update the payment in the ScheduledPaymentDetails array
                    if (!this.globalFunctions.isEmpty(updatedPaymentDetails) && !this.globalFunctions.isEmpty(updatedPaymentDetails.GUID)) {
                        const paymentIndex = this.ScheduledPaymentDetails.findIndex(p => p.GUID === updatedPaymentDetails.GUID);
                        if (paymentIndex !== -1) {

                            //Update the payment with the new next payment date
                            this.ScheduledPaymentDetails[paymentIndex].NextPaymentDate = this.globalFunctions.getCustomDateFormat(nextPaymentDate, "shortdate", "custom", "YYYY-MM-DD");

                            //Reset the selected payment GUID
                            this.SelectedPaymentGUID = null;

                            //Reset editing state
                            this.IsPaymentBeingEdited = true;

                            //Prompt user if they want to set up a One Off or Customer Nominated Payment
                            const confirmModalDialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Set Up Customer Nominated Payment", 0, true, false);

                            //Set prompt message
                            confirmModalDialogRef.DialogRef.componentInstance.htmlContent = "Scheduled payment has been deferred successfully. Would you like to set up a One Off or Customer Nominated Payment?";

                            //Set button text to "Yes" (using the correct property name)
                            confirmModalDialogRef.DialogRef.componentInstance.YesButtonText = "Yes";

                            //The "No" button is shown by default, so we don't need to set ShowNoButton = true
                            //Handle the user's response
                            confirmModalDialogRef.DialogRef.afterClosed().subscribe((result: boolean) => {
                                if (result === true) {

                                    //If user clicks "Yes", launch the Customer Nominated Payment setup
                                    this.CustomerNominatedPaymentSetup_Click();
                                }
                                else {
                                    //Refresh system tasks next payment date once payement date is deferred
                                    this.LoanIndex.Entity_FullRefresh("PendingTasks");

                                    //Refresh notes once payement date is deferred
                                    this.LoanIndex.Entity_FullRefresh("LoanNotes");
                                }
                            });
                        }
                    }
                }
            });

        } else if (modifyType === "ChangeFrequency") {

            //Set page title
            paymentManagementModifyModalRef.DialogRef.componentInstance.PageTitle = "Change Payment Frequency";
            paymentManagementModifyModalRef.DialogRef.componentInstance.ModifyType = "ChangeFrequency";

            //Pass the customer nominated frequency options to the modal
            paymentManagementModifyModalRef.DialogRef.componentInstance.CustomerNominatedFrequencyOptions = this.CustomerNominatedFrequencyOptions;

            //If a payment was passed, set it as the selected payment
            if (!this.globalFunctions.isEmpty(selectedPayment)) {
                paymentManagementModifyModalRef.DialogRef.componentInstance.SelectedPaymentDetails = selectedPayment;
            }

            //Subscribe to the dialog close event
            paymentManagementModifyModalRef.DialogRef.afterClosed().subscribe((result: { updatedFrequencyDetails: { UpdatedPaymentDetails: any; }; }) => {
                if (!this.globalFunctions.isEmpty(result) && !this.globalFunctions.isEmpty(result.updatedFrequencyDetails)) {

                    //Get the updated payment details
                    const updatedPaymentDetails = result.updatedFrequencyDetails.UpdatedPaymentDetails;

                    //Find and update the payment in the ScheduledPaymentDetails array
                    if (!this.globalFunctions.isEmpty(updatedPaymentDetails)) {

                        //Update the scheduled details with updated details
                        this.ScheduledPaymentDetail_Update(updatedPaymentDetails, "Payment frequency has been changed successfully", "Payment Frequency");

                        //Refresh system tasks once frequency is changed
                        this.LoanIndex.Entity_FullRefresh("PendingTasks");

                        //Refresh notes once frequency is changed
                        this.LoanIndex.Entity_FullRefresh("LoanNotes");
                    }
                }
            });
        }
    }

    //Handle click event for Customer Nominated Payment Setup button
    public CustomerNominatedPaymentSetup_Click(): void {

        //Create an api request to retrieve new monthly scheduled payment details
        const apiRequest = {
            AccountID: this.TargetAccountID
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Hide the overlay panel after launching the payment setup
        this.OverlayPanelNewPayment.hide();

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.GetChargeAndPaymentTransactionTypes], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {

                    //Get the response
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    if (response != null) {

                        this.PaymentTransactionOptions = response.AccountPaymentTransactionTypes;
                        this.ChargeTransactionOptions = response.AccountChargeTransactionTypes;

                        //Unescape any HTML encoded values
                        if (this.globalFunctions.isEmpty(this.ChargeTransactionOptions)) {
                            // If there was no AccountChargeTransactionTypes n the response, add Not Applicable to indicate this is the case. per LE see XV8-5720
                            this.ChargeTransactionOptions = [];
                            this.ChargeTransactionOptions.push({ ControlGUID: "Not Applicable", ControlValue: "Not Applicable", ControlType: "Charge Transaction", ControlDisplay: "Not Applicable", Classifications: "" });
                        }
                        else {
                            this.ChargeTransactionOptions.forEach(chargeTxn => {
                                chargeTxn.ControlValue = this.globalFunctions.HTMLUnescape(chargeTxn.ControlValue);
                                chargeTxn.ControlDisplay = this.globalFunctions.HTMLUnescape(chargeTxn.ControlDisplay);
                            });
                        }

                        if (!this.globalFunctions.isEmpty(this.PaymentTransactionOptions)) {
                            this.PaymentTransactionOptions.forEach(paymentTxn => {
                                paymentTxn.ControlValue = this.globalFunctions.HTMLUnescape(paymentTxn.ControlValue);
                                paymentTxn.ControlDisplay = this.globalFunctions.HTMLUnescape(paymentTxn.ControlDisplay);
                            });
                        }

                        //Launch modify modal
                        this.PaymentManagementModify_Launch("CustomerNominatedPayment");

                    }
                    else {

                        //Error notification
                        this.notifyService.Error_Show("Unable to retrieve payment transaction types.", "Customer Nominated Payment");
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }

    //Handle click event for Scheduled Payment Setup button
    public ScheduledPaymentSetup_Click(): void {

        //Check if Direct Debit Task is configured
        if (!this.DirectDebitEnabled) {
            this.notifyService.Error_Show("Missing Direct Debit Task configuration.", "Configuration Error");
            return;
        }

        //Create an api request to retrieve new monthly scheduled payment details
        const apiRequest = {
            AccountID: this.TargetAccountID
        };

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.GetNewScheduledPayment], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {
                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
                else {

                    //Get the response
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Check if we got the new scheduled payment details
                    if (response == null || response.NewScheduledPayment == null) {

                        //Error notification
                        this.notifyService.Error_Show("Unable to retrieve new scheduled payment details.", "Scheduled Payment");

                        //Turn off the fullscreen loading
                        this.clientDataStore.SetShowFullscreenLoading(false);
                        this.IsGenerateInProgress = false;

                        return;
                    }

                    //Set the schedule frequency options with the server response
                    if (response.ScheduleFrequencyOptions != null) {
                        this.ScheduleFrequencyOptions = response.ScheduleFrequencyOptions;
                    }

                    //Set the payment type options with the server response
                    if (response.PaymentMethodOptions != null) {
                        this.PaymentMethodOptions = response.PaymentMethodOptions;
                    }

                    //Pass the existing scheduled payments to the modal
                    this.MonthlyInstalmentAmount = response.NewScheduledPayment.MonthlyInstalmentAmount;
                    this.MonthlyRepaymentAmount = response.NewScheduledPayment.MonthlyRepaymentAmount;
                    this.FortnightlyRepaymentAmount = response.NewScheduledPayment.FortnightlyRepaymentAmount;
                    this.WeeklyRepaymentAmount = response.NewScheduledPayment.WeeklyRepaymentAmount;
                    this.NewInstalmentDueDate = response.NewScheduledPayment.NextScheduledPaymentDate;
                    this.NewSchedulePaymentFrequency = this.globalFunctions.HTMLUnescape(response.NewScheduledPayment.MonthlyPaymentFrequencyValue);
                    this.MonthlyPaymentFrequencyGUID = response.NewScheduledPayment.MonthlyPaymentFrequencyGUID;

                    //Launch modify modal
                    this.PaymentManagementModify_Launch("ScheduledPayment");

                    //Hide the overlay panel after launching the payment setup
                    this.OverlayPanelNewPayment.hide();

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }

    //Handle click event for Defer Payment button
    public DeferPayment_Click(payment: { GUID: string }): void {
        if (!this.globalFunctions.isEmpty(payment)) {
            this.PaymentManagementModify_Launch("DeferPayment", payment);
        }
    }

    //Handle click event for Edit Payment button
    public Payment_Modify(payment: { GUID: string }): void {

        //Find the matching payment details
        const selectedPayment = this.ScheduledPaymentDetails.find(p => p.GUID === payment.GUID);
        if (!this.globalFunctions.isEmpty(selectedPayment)) {
            selectedPayment.AllowEditing = true; // Allow editing for the selected payment

            this.SelectPayment_Row(payment.GUID);
        }

        this.IsPaymentBeingEdited = true;
    }

    //Handle click event for Cancel Payment Edit button
    public Payment_Cancel(payment: { GUID: string }): void {

        //Find the matching payment details
        const selectedPayment = this.ScheduledPaymentDetails.find(p => p.GUID === payment.GUID);
        if (!this.globalFunctions.isEmpty(selectedPayment)) {

            //Allow editing for the selected payment
            selectedPayment.AllowEditing = false;

            //Reset the selected payment GUID
            this.SelectedPaymentGUID = null;
        }

        //Reset the editing state
        this.IsPaymentBeingEdited = false;
    }

    //Handle click event for Delete Payment button
    public Payment_Delete(payment: { GUID: string }): void {

        //Do a confirm screen for this
        const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Confirm Modal", 0, true, false);

        //Use html content so that we can style it
        dialogRef.DialogRef.componentInstance.htmlContent = "Are you sure that you want to delete this payment?"

        dialogRef.DialogRef.afterClosed().subscribe((result: boolean) => {
            if (result === true) {

                //Construct an apirequest
                const apiRequest = {
                    AccountID: this.TargetAccountID,
                    ScheduledPaymentGUID: payment.GUID,
                    PaymentType: "Customer Nominated",
                    ModifyType: "Delete"
                };

                //Track state of this request, so that we can show the spinner, if necessary.
                this.IsGenerateInProgress = true;
                this.clientDataStore.SetShowFullscreenLoading(true);

                //Invoke API Endpoint to delete the payment
                this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.ModifyScheduledPayment], apiRequest)
                    .subscribe(apiResponse => {
                        if (this.globalFunctions.isEmpty(apiResponse)) {

                            //There was no response, or an error.
                            this.IsGenerateInProgress = false;
                            this.clientDataStore.SetShowFullscreenLoading(false);

                            return;
                        }
                        else {

                            //We got the response. Lets remove the payment from the array
                            // Find the index of the payment to remove
                            const paymentIndex = this.ScheduledPaymentDetails.findIndex(p => p.GUID === payment.GUID);

                            // If the payment is found, remove it from the array
                            if (paymentIndex !== -1) {
                                this.ScheduledPaymentDetails.splice(paymentIndex, 1);

                                // Reset editing state and selected payment
                                this.IsPaymentBeingEdited = false;
                                this.SelectedPaymentGUID = null;

                                // Show success notification
                                this.notifyService.Success_Show("Payment has been deleted", "Customer Nominated Payment");
                            }

                            //Turn off the fullscreen loading
                            this.clientDataStore.SetShowFullscreenLoading(false);
                            this.IsGenerateInProgress = false;
                            return;
                        }
                    });
            }
        });
    }

    //Get font text css    
    public PaymentCSS_Get(payment: { IsEnabled: boolean, GUID: string }): string {

        //Set the CSS class for white text on dark background by default
        let cssClass = "glb_whiteTextDarkBG";

        //Check if the direct debit is disabled
        if (payment.IsEnabled === false) {
            cssClass = "glb_disabledRow";
        }

        //Check if the payment is selected
        if (this.SelectedPaymentGUID === payment.GUID) {
            cssClass += " glb_selectedRow"; // Add selected row class
        }

        //Return the CSS class
        return cssClass;
    }

    //Handle click event for Disable Scheduled Payment button
    public Payment_Disable(payment: { GUID: string }): void {

        //Do a confirm screen for this, similar to delete payment
        const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Confirm Modal", 0, true, false);

        //Use html content so that we can style it
        dialogRef.DialogRef.componentInstance.htmlContent = "Are you sure that you want to disable this scheduled payment?";

        dialogRef.DialogRef.afterClosed().subscribe((result: boolean) => {
            if (result === true) {

                //Find the payment to disable
                const paymentIndex = this.ScheduledPaymentDetails.findIndex(p => p.GUID === payment.GUID);

                //If the payment is found, disable it
                if (paymentIndex !== -1) {

                    //Reset editing state and selected payment
                    this.IsPaymentBeingEdited = true;
                    this.SelectedPaymentGUID = null;

                    //Disable the direct debit
                    this.IsDirectDebitEnabled = false;

                    //API call to disable the direct debit
                    this.DirectDebitFlag_Update(this.IsDirectDebitEnabled, paymentIndex);
                }
            }
        });
    }

    //Handle click event for Enable Scheduled Payment button
    public Payment_Enable(payment: { GUID: string }): void {

        //Do a confirm screen for this, similar to disable payment
        const dialogRef = this.globalFunctions.FeatureModal_Launch(ConfirmModal, this.globalFunctions.GetConfirmModalConfig(), this.dialog, "Confirm Modal", 0, true, false);

        //Use html content so that we can style it
        dialogRef.DialogRef.componentInstance.htmlContent = "Are you sure that you want to enable this scheduled payment?";

        dialogRef.DialogRef.afterClosed().subscribe((result: boolean) => {
            if (result === true) {

                //Find the payment to enable
                const paymentIndex = this.ScheduledPaymentDetails.findIndex(p => p.GUID === payment.GUID);

                // If the payment is found, enable it
                if (paymentIndex !== -1) {

                    // Reset editing state and selected payment
                    this.IsPaymentBeingEdited = true;
                    this.SelectedPaymentGUID = null;

                    //Enable the direct debit
                    this.IsDirectDebitEnabled = true;

                    //API call to enable the direct debit
                    this.DirectDebitFlag_Update(this.IsDirectDebitEnabled, paymentIndex);
                }
            }
        });
    }

    //Handle click event for Change Frequency button
    public ChangeFrequency_Click(payment: any): void {
        if (!this.globalFunctions.isEmpty(payment)) {
            this.PaymentManagementModify_Launch("ChangeFrequency", payment);
        }
    }

    //Show the fee details modal
    public ShowFeeDetails_Click(template: TemplateRef<any>, schedulePayment: any): void {

        //Generate a unique identifier
        this.FeeModalIdentifier = this.globalFunctions.GenerateFastGUID();

        //Set the selected payment for the modal
        this.SelectedPayment = schedulePayment;

        if (!this.globalFunctions.isEmpty(this.SelectedPayment) && !this.globalFunctions.isEmpty(this.SelectedPayment.ScheduledPaymentFeeList) && this.SelectedPayment.ScheduledPaymentFeeList.length !== 0) {

            this.TotalFeeAmount = 0;

            //Calculate the total consolidated fee amount and format the individual fee amounts
            this.SelectedPayment.ScheduledPaymentFeeList.forEach(fee => {
                fee.ProcessAmount_lbl = fee.ProcessAmount != null ? this.globalFunctions.customDataTypeParser(fee.ProcessAmount, "currency.2", "aus") : "";
                this.TotalFeeAmount += fee.ProcessAmount != null ? parseFloat(fee.ProcessAmount) : 0;
            });

            //Format the total consolidated fee amount
            this.TotalConsolidatedFeeAmount_lbl = this.globalFunctions.customDataTypeParser(this.TotalFeeAmount, "currency.2", "aus");
        }

        //Wrapper needed to avoid circular dependencies
        this.DialogRef = this.globalFunctions.FeatureModal_Launch(template, this.globalFunctions.GetFeatureModalConfig('40%'), this.dialog, "View Scheduled Fee Details", 0, true, false, this.FeeModalIdentifier);
    }

    //Construct a new payment object with the updated details
    private SchedulePaymentDetail_Construct(schedulePaymentDetails: any) {

        //Construct a new payment object with the updated details
        const paymentDetails = {
            GUID: schedulePaymentDetails.ProductLoanTransferGUID,
            PendingTaskGUID: schedulePaymentDetails.PendingTaskGUID,
            FrequencyTaskGUID: schedulePaymentDetails.FrequencyTaskGUID,
            Frequency: schedulePaymentDetails.Frequency,
            FrequencyTypeClassification: schedulePaymentDetails.FrequencyTypeClassification,
            DirectDebitAmount_lbl: schedulePaymentDetails.DirectDebitAmount != null ? this.globalFunctions.customDataTypeParser(schedulePaymentDetails.DirectDebitAmount, "currency.2", "aus") : "",
            InstalmentAmount_lbl: schedulePaymentDetails.PaymentType !== "Scheduled" ? "N/A" :
                (schedulePaymentDetails.InstalmentAmount != null ? this.globalFunctions.customDataTypeParser(schedulePaymentDetails.InstalmentAmount, "currency.2", "aus") : ""),
            NextPaymentDate: schedulePaymentDetails.NextPaymentDueDate != null ? this.globalFunctions.getCustomDateFormat(schedulePaymentDetails.NextPaymentDueDate, "shortdate", "custom", "YYYY-MM-DD") : "",
            ExpiryDate: schedulePaymentDetails.ExpiryDate != null ? this.globalFunctions.getCustomDateFormat(schedulePaymentDetails.ExpiryDate, "shortdate", "custom", "YYYY-MM-DD") : "",
            InstalmentDueDate: schedulePaymentDetails.InstalmentDueDate != null ? this.globalFunctions.getCustomDateFormat(schedulePaymentDetails.InstalmentDueDate, "shortdate", "custom", "YYYY-MM-DD") : "",
            PaymentType: schedulePaymentDetails.PaymentType,
            Notes: schedulePaymentDetails.Notes ?? "",
            IsEnabled: schedulePaymentDetails.IsDisabled == 0 ? true : false,
            AllowEditing: false,
            PaymentMethod: schedulePaymentDetails.PaymentMethod,
            ConsolidatedFeeAmount: schedulePaymentDetails.ConsolidatedFeeAmount,
            ConsolidatedFeeAmount_lbl: schedulePaymentDetails.PaymentType !== "Scheduled" ? "N/A" : (schedulePaymentDetails.ConsolidatedFeeAmount != null ? this.globalFunctions.customDataTypeParser(schedulePaymentDetails.ConsolidatedFeeAmount, "currency.2", "aus") : ""),
            ScheduledPaymentFeeList: schedulePaymentDetails.ScheduledPaymentFeeList
        }

        return paymentDetails;
    }

    //Update the scheduled payment details with the updated details
    private ScheduledPaymentDetail_Update(updatedPaymentDetails: any, toastMessage: string, toastTitle: string): void {

        //Find and update the payment in the ScheduledPaymentDetails array
        if (!this.globalFunctions.isEmpty(updatedPaymentDetails)) {

            //Construct the payment details object to update
            const paymentDetailsToUpdate = this.SchedulePaymentDetail_Construct(updatedPaymentDetails);

            //Find the index of the payment to update
            const paymentIndex = this.ScheduledPaymentDetails.findIndex(p => p.GUID === paymentDetailsToUpdate.GUID);

            if (paymentIndex !== -1) {

                //Update the payment with the new frequency and other details
                this.ScheduledPaymentDetails[paymentIndex] = paymentDetailsToUpdate;

                //Reset the selected payment GUID
                this.SelectedPaymentGUID = null;

                //Reset the payment being edited status
                this.IsPaymentBeingEdited = false;

                //Show success notification
                this.notifyService.Success_Show(toastMessage, toastTitle);
            }
        }
    }
}
